import { ROUTE_SIGN_UP } from '@/constants/routes';
import { TOAST_DEFAULT_OPTIONS } from '@/constants/toast';
import useAnalyticsCalls from '@/hooks/use-analytics';
import { useSignInMutation, useVerifyOTPMutation } from '@/queries/auth.queries';
import { UserLevelType } from '@/repositories/me.repository';
import authService from '@/services/auth.service';
import { getRoute } from '@/utils';
import { getSafeRedirect } from '@/utils/safe-redirect';
import { useToast } from '@cmpsr/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';

export default function useVerifyOTPController() {
  const verifyOTPMutation = useVerifyOTPMutation();
  const signInMutation = useSignInMutation();
  const [params, setParams] = useSearchParams();
  const analytics = useAnalyticsCalls();
  const navigate = useNavigate();
  const form = useForm({
    defaultValues: { otp: '' },
    resolver: zodResolver(z.object({ otp: z.string().min(6).max(6) })),
  });
  const toast = useToast();
  const [countdown, setCountdown] = useState(60);

  useEffect(() => {
    const timeout = setInterval(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);

    return () => clearTimeout(timeout);
  }, []);

  function _validatedParam(name: string): string {
    const value = params.get(name);
    if (!value) {
      throw new Error(`Invalid URL. Missing parameter: ${name}`);
    }
    return value;
  }

  const verificationId = _validatedParam('id');
  const email = _validatedParam('email');
  const type = _validatedParam('type') as 'email' | 'sms';

  function handleBack() {
    return navigate(-1);
  }

  function handleResendCode() {
    signInMutation
      .mutateAsync({ credential: email, credentialType: type })
      .then((res) => {
        setParams((prev) => {
          prev.set('id', res.id);
          return prev;
        });
        setCountdown(60);
        form.reset();
        toast({
          ...TOAST_DEFAULT_OPTIONS,
          title: 'Success',
          description: 'A new code has been sent!',
          status: 'success',
        });
      })
      .catch((error) => {
        console.error(error);
        toast({
          ...TOAST_DEFAULT_OPTIONS,
          title: `Error ${error.response?.data?.statusCode ?? ''}`,
          description: error.response?.data?.message ?? error.message,
          status: 'error',
        });
      });
  }

  // Get redirect destination from URL params (e.g., from expired magic link)
  const redirectTo = getSafeRedirect(params.get('redirect'));

  const handleSubmit = form.handleSubmit((values) => {
    verifyOTPMutation
      .mutateAsync({ id: verificationId, code: values.otp })
      .then((res) => {
        authService.login(res);

        if (res.userLevel === UserLevelType.waitlist) {
          return navigate(getRoute(ROUTE_SIGN_UP));
        }

        // Navigate to redirect destination or dashboard
        navigate(redirectTo);

        analytics.callIdentifyEmail(res.userId, email);
        analytics.callSignInCompleted();
      })
      .catch((error) => {
        console.error(error);
        form.reset();
        form.setError('otp', { message: error.response.data?.message ?? error.response.statusText });
      });
  });

  return {
    form,
    handleSubmit,
    email,
    handleResendCode,
    countdown,
    showResend: countdown <= 0,
    handleBack,
  };
}
